import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import type { ReactNode } from "react";
import { useId } from "react";
import cx from "ui/cx";
import Label from "ui/forms/Label";
import { useOneClickPerActivation } from "ui/forms/oneClickPerActivation";
import styles from "./Switch.module.scss";

export type SwitchSize = "default" | "small";

/**
 * `render` and `nativeButton` are withheld: the wrapper owns the host, and a native `<button>`
 * host breaks the one-click invariant. Base UI's root re-dispatches a click from its hidden input
 * whether or not the host is native, and a native host also gets the browser's own click for
 * Enter and Space, so keyboard activation reaches an ancestor twice. See `useOneClickPerActivation`.
 */
export interface SwitchProps
  extends Omit<
    React.ComponentProps<typeof SwitchPrimitive.Root>,
    "onCheckedChange" | "size" | "render" | "nativeButton"
  > {
  label?: ReactNode;
  className?: string;
  onCheckedChange?: (checked: boolean) => void;
  onChangeBoolean?: (checked: boolean) => void;
  tooltip?: ReactNode;
  size?: SwitchSize;
}

export default function Switch({
  label,
  disabled = false,
  className,
  checked,
  size = "default",
  onChangeBoolean, // use for boolean state only
  onCheckedChange, // use for full state compatibility
  tooltip,
  ...props
}: SwitchProps) {
  const id = useId();
  const isDisabled = disabled;

  const handleCheckedChange = (newChecked: boolean) => {
    if (!isDisabled) {
      onCheckedChange?.(newChecked);
      if (!onCheckedChange) {
        onChangeBoolean?.(newChecked);
      }
    }
  };

  const controlled = !onCheckedChange && !onChangeBoolean;

  // Collapses Base UI's real-plus-re-dispatched click pair down to one per press for ancestors.
  const oneClickPerActivation = useOneClickPerActivation();

  return (
    <div
      className={cx(
        styles.wrapper,
        isDisabled && styles.disabledBase,
        !isDisabled && !controlled && styles.clickable,
        disabled && styles.disabled,
        size === "small" && styles.small,
        className,
      )}
      data-testid="switch-wrapper"
      {...oneClickPerActivation}
    >
      <SwitchPrimitive.Root
        id={id}
        checked={checked}
        onCheckedChange={handleCheckedChange}
        disabled={isDisabled}
        className={styles.root}
        data-testid="switch-input"
        {...props}
      >
        <SwitchPrimitive.Thumb className={styles.thumb} data-testid="switch-thumb" />
      </SwitchPrimitive.Root>
      {label && (
        <Label
          // if controlled (no onChange handlers), don't set the htmlFor attribute so that the label is not clickable
          htmlFor={!controlled ? id : undefined}
          disabled={isDisabled}
          data-testid="switch-label"
          tooltip={tooltip}
        >
          {label}
        </Label>
      )}
    </div>
  );
}
